import { Warning } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import { Loading } from 'src/components/Loading';
import { withIntegrationApp } from 'src/hocs/withIntegrationApp';
import { Can, Subject } from 'src/hooks/usePermissions';
import {
  IntegrationKey,
  PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS,
} from 'src/services/integration-app.service';
import { CampaignOverviewTab } from './beta/CampaignOverview/types';
import { ExtraStepConfiguration } from './components/FieldMapping/ExtraStepConfiguration';
import { FieldMapping } from './components/FieldMapping/FieldMapping';
import { PreviewImport } from './components/FieldMapping/PreviewImport';
import { ContactSegmentSelector } from './components/ui/ContactSegmentSelector';
import { useCampaignContext } from './contexts/CampaignContext';
import { useContacts } from './contexts/ContactsContext';
import { useCrmSync } from './contexts/CrmSyncContext';
import { CrmSyncProviders } from './contexts/CrmSyncProviders';
import { useFieldMapping } from './contexts/FieldMappingContext';

const AddContactsFromCrmContent = () => {
  const location = useLocation();

  const { currentStep, integratedCrm } = useCrmSync();
  const { campaign, isInitializing: isCampaignLoading } = useCampaignContext();
  const { isLoading: isContactsLoading, isImporting } = useContacts();
  const { isInitialized: isFieldMappingInitialized } = useFieldMapping();

  const isBeta = location.pathname.includes('beta');

  // Show specific loading messages based on what's happening
  if (isCampaignLoading) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Loading campaign...</p>
      </div>
    );
  }

  if (isImporting) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Importing contacts...</p>
      </div>
    );
  }

  if (isContactsLoading) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Loading contacts...</p>
      </div>
    );
  }

  if (!isFieldMappingInitialized && currentStep === 'mapping') {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Loading field mapping...</p>
      </div>
    );
  }

  if (!integratedCrm) {
    return (
      <div className="flex items-center gap-2 p-6">
        <Warning className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <span className="text-base text-amber-700 font-medium">
          CRM integration hasn't been configured. Set it up in the{' '}
          <Link
            to={`${isBeta ? '/beta' : ''}/campaigns/${isBeta ? 'overview' : 'edit'}/${campaign?.referenceId}${isBeta ? `?tab=${CampaignOverviewTab.SETTINGS}` : ''}`}
            className="font-semibold underline hover:text-amber-900"
          >
            campaign settings
          </Link>
          .
        </span>
      </div>
    );
  }

  if (currentStep === 'configuration') {
    const shouldShowSegmentSelector =
      integratedCrm &&
      PROVIDERS_SUPPORTING_SEGMENTED_CONTACTS.includes(
        integratedCrm.value as IntegrationKey,
      );

    if (shouldShowSegmentSelector) {
      return (
        <ExtraStepConfiguration>
          <ContactSegmentSelector />
        </ExtraStepConfiguration>
      );
    }
  }

  return (
    <>
      {currentStep === 'preview' && <PreviewImport />}
      {currentStep === 'configuration' && <ExtraStepConfiguration />}
      {currentStep === 'mapping' && campaign && <FieldMapping />}
    </>
  );
};

const AddContactsFromCrm = () => {
  return (
    <Can operation="read" subject={Subject.CAMPAIGNS}>
      <CrmSyncProviders>
        <AddContactsFromCrmContent />
      </CrmSyncProviders>
    </Can>
  );
};

export default withIntegrationApp(AddContactsFromCrm);
